import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert,
    alpha,
    Box,
    Button,
    Chip,
    Container,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import { getClassStudents } from "../../redux/sclassRelated/sclassHandle";
import { StyledTableCell, StyledTableRow } from "../../components/styles";
import { useFormTeacherHomeroom } from "../../hooks/useFormTeacherHomeroom";

const statusColor = {
    Verified: "success",
    Pending: "warning",
    Rejected: "error",
};

const TeacherMyClassStudents = () => {
    const dispatch = useDispatch();
    const { sclassStudents, loading } = useSelector((state) => state.sclass);
    const { homeroomClassId, homeroomClassName, hasHomeroomClass } = useFormTeacherHomeroom();

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);

    useEffect(() => {
        if (homeroomClassId) dispatch(getClassStudents(homeroomClassId));
    }, [dispatch, homeroomClassId]);

    const rows = useMemo(() => {
        if (!Array.isArray(sclassStudents)) return [];
        return sclassStudents.map((s) => ({
            id: s._id || s.id,
            name: s.name,
            rollNum: s.rollNum,
            status: s.enrollmentStatus || "Verified",
        }));
    }, [sclassStudents]);

    const paginated = useMemo(
        () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [rows, page, rowsPerPage],
    );

    if (!hasHomeroomClass) {
        return (
            <Container maxWidth="md" sx={{ py: 3 }}>
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    No class linked. Go to <strong>My class</strong> for setup instructions.
                </Alert>
                <Button component={RouterLink} to="/Teacher/my-class" sx={{ mt: 2 }}>
                    Back to My class
                </Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Button
                    component={RouterLink}
                    to="/Teacher/my-class"
                    startIcon={<ArrowBackIcon />}
                    size="small"
                >
                    My class
                </Button>
            </Stack>

            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    mb: 2,
                    borderRadius: 2.5,
                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                }}
            >
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ sm: "center" }}
                    spacing={2}
                >
                    <Box>
                        <Typography variant="h5" fontWeight={800}>
                            Students — {homeroomClassName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Pending rows need director verification before fees apply.
                        </Typography>
                    </Box>
                    <Button
                        component={RouterLink}
                        to="/Teacher/enroll-student"
                        variant="contained"
                        startIcon={<PersonAddAlt1Icon />}
                    >
                        Enroll student
                    </Button>
                </Stack>
            </Paper>

            <Paper
                elevation={0}
                sx={{ borderRadius: 2.5, border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}` }}
            >
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Name</StyledTableCell>
                                <StyledTableCell>Roll no.</StyledTableCell>
                                <StyledTableCell>Enrollment</StyledTableCell>
                                <StyledTableCell align="right">Profile</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading
                                ? [1, 2, 3, 4].map((i) => (
                                      <TableRow key={i}>
                                          <StyledTableCell colSpan={4}>
                                              <Skeleton height={36} />
                                          </StyledTableCell>
                                      </TableRow>
                                  ))
                                : paginated.map((row) => (
                                      <StyledTableRow key={row.id}>
                                          <StyledTableCell>{row.name}</StyledTableCell>
                                          <StyledTableCell>{row.rollNum}</StyledTableCell>
                                          <StyledTableCell>
                                              <Chip
                                                  size="small"
                                                  label={row.status}
                                                  color={statusColor[row.status] || "default"}
                                                  variant={row.status === "Pending" ? "filled" : "outlined"}
                                              />
                                          </StyledTableCell>
                                          <StyledTableCell align="right">
                                              <Tooltip title="View student">
                                                  <IconButton
                                                      size="small"
                                                      component={RouterLink}
                                                      to={`/Teacher/class/student/${row.id}`}
                                                  >
                                                      <VisibilityOutlinedIcon fontSize="small" />
                                                  </IconButton>
                                              </Tooltip>
                                          </StyledTableCell>
                                      </StyledTableRow>
                                  ))}
                            {!loading && rows.length === 0 ? (
                                <TableRow>
                                    <StyledTableCell colSpan={4}>
                                        <Typography color="text.secondary" sx={{ py: 2 }}>
                                            No students yet. Use Enroll student to add your first pupil.
                                        </Typography>
                                    </StyledTableCell>
                                </TableRow>
                            ) : null}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={rows.length}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[10, 15, 25, 50]}
                />
            </Paper>
        </Container>
    );
};

export default TeacherMyClassStudents;
